/**
 * 萨米板棋（Tablut）游戏逻辑
 * 9×9棋盘，护王方国王和8名守卫vs攻击方16名攻击者
 * 护王方胜利：国王到达任一边角
 * 攻击方胜利：将国王四面围住（捕获）
 */

import {
  TablutGameState,
  TablutPiece,
  TablutMove,
  Position,
  GameMode,
  Difficulty,
  TABLUT_CONFIG,
  TablutPieceType
} from '../types/game';

// 初始布局常量
export const INITIAL_LAYOUT = {
  // 国王位置（中心王座）
  king: { row: 4, col: 4 },

  // 守卫位置（围绕国王形成十字形）
  guards: [
    { row: 4, col: 2 }, { row: 4, col: 3 }, { row: 4, col: 5 }, { row: 4, col: 6 }, // 水平线
    { row: 2, col: 4 }, { row: 3, col: 4 }, { row: 5, col: 4 }, { row: 6, col: 4 }  // 垂直线
  ],

  // 攻击者位置（四面包围）
  attackers: [
    // 上方
    { row: 0, col: 3 }, { row: 0, col: 4 }, { row: 0, col: 5 },
    { row: 1, col: 4 },
    // 下方
    { row: 8, col: 3 }, { row: 8, col: 4 }, { row: 8, col: 5 },
    { row: 7, col: 4 },
    // 左侧
    { row: 3, col: 0 }, { row: 4, col: 0 }, { row: 5, col: 0 },
    { row: 4, col: 1 },
    // 右侧
    { row: 3, col: 8 }, { row: 4, col: 8 }, { row: 5, col: 8 },
    { row: 4, col: 7 }
  ]
};

// 特殊位置
export const THRONE: Position = { row: 4, col: 4 }; // 王座
export const CORNERS: Position[] = [
  { row: 0, col: 0 }, { row: 0, col: 8 },
  { row: 8, col: 0 }, { row: 8, col: 8 }
]; // 四个角落

// 创建初始游戏状态
export const createInitialGameState = (
  gameMode: GameMode,
  difficulty?: Difficulty
): TablutGameState => {
  // 创建9x9棋盘
  const board: (TablutPiece | null)[][] = Array.from({ length: 9 }, () => Array(9).fill(null));

  // 放置国王
  board[INITIAL_LAYOUT.king.row][INITIAL_LAYOUT.king.col] = {
    type: 'king',
    player: 'defender'
  };

  // 放置守卫
  INITIAL_LAYOUT.guards.forEach(pos => {
    board[pos.row][pos.col] = {
      type: 'guard',
      player: 'defender'
    };
  });

  // 放置攻击者
  INITIAL_LAYOUT.attackers.forEach(pos => {
    board[pos.row][pos.col] = {
      type: 'attacker',
      player: 'attacker'
    };
  });

  return {
    board,
    currentPlayer: 'defender', // 护王方先手
    status: 'playing',
    winner: null,
    moves: [],
    gameHistory: [],
    gameMode,
    gameType: 'tablut',
    difficulty,
    isAIThinking: false,
    selectedPiece: undefined,
    possibleMoves: [],
    capturedPieces: { defender: [], attacker: [] },
    kingPosition: INITIAL_LAYOUT.king,
    corners: CORNERS,
    throne: THRONE,
    totalMoves: 0,
    captureMoves: 0,
    kingEscapeAttempts: 0,
    kingEscaped: false,
    kingCaptured: false
  };
};

// 检查位置是否在棋盘内
export const isValidPosition = (position: Position): boolean => {
  return position.row >= 0 && position.row < 9 && position.col >= 0 && position.col < 9;
};

// 检查两个位置是否相邻（只能直线移动，不能斜向）
export const areAdjacent = (from: Position, to: Position): boolean => {
  const rowDiff = Math.abs(from.row - to.row);
  const colDiff = Math.abs(from.col - to.col);

  // 只能水平或垂直移动一格
  return (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);
};

// 获取棋子的所有可能移动
export const getAllPossibleMoves = (gameState: TablutGameState, player: 'defender' | 'attacker'): TablutMove[] => {
  const moves: TablutMove[] = [];

  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      const piece = gameState.board[row][col];

      if (piece && piece.player === player) {
        // 获取该棋子的可能移动
        const pieceMoves = getPossibleMovesForPiece(gameState, { row, col });
        moves.push(...pieceMoves);
      }
    }
  }

  return moves;
};

// 获取特定棋子的可能移动
export const getPossibleMovesForPiece = (gameState: TablutGameState, position: Position): TablutMove[] => {
  const piece = gameState.board[position.row][position.col];
  if (!piece) return [];

  const moves: TablutMove[] = [];
  const directions = [
    { row: -1, col: 0 }, // 上
    { row: 1, col: 0 },  // 下
    { row: 0, col: -1 }, // 左
    { row: 0, col: 1 }   // 右
  ];

  for (const dir of directions) {
    let currentPos = { ...position };

    // 沿着方向移动直到遇到障碍
    while (true) {
      currentPos = {
        row: currentPos.row + dir.row,
        col: currentPos.col + dir.col
      };

      // 检查是否在棋盘内
      if (!isValidPosition(currentPos)) break;

      // 萨米板棋特殊规则：
      // 1. 只有国王可以走到角落
      if (isCorner(currentPos) && piece.type !== 'king') {
        break; // 不能继续这个方向的移动
      }

      // 2. 只有国王可以走到中心点（王座）
      if (isThrone(currentPos) && piece.type !== 'king') {
        break; // 不能继续这个方向的移动
      }

      const targetPiece = gameState.board[currentPos.row][currentPos.col];

      // 如果目标位置为空，可以移动
      if (!targetPiece) {
        moves.push({
          player: piece.player,
          from: position,
          to: currentPos,
          timestamp: Date.now()
        });
      } else {
        // 遇到棋子，停止移动
        break;
      }
    }
  }

  return moves;
};

// 检查是否为中心点或角落
export const isThrone = (position: Position): boolean => {
  return position.row === 4 && position.col === 4;
};

export const isCorner = (position: Position): boolean => {
  return CORNERS.some(corner => corner.row === position.row && corner.col === position.col);
};

// 检查移动是否有效
export const isValidMove = (gameState: TablutGameState, move: TablutMove): boolean => {
  const piece = gameState.board[move.from.row][move.from.col];

  if (!piece || piece.player !== gameState.currentPlayer) return false;
  if (!isValidPosition(move.to)) return false;

  const targetPiece = gameState.board[move.to.row][move.to.col];
  if (targetPiece) return false; // 目标位置必须有空位

  // 萨米板棋特殊规则：
  // 1. 只有国王可以走到角落
  if (isCorner(move.to) && piece.type !== 'king') {
    return false;
  }

  // 2. 只有国王可以走到中心点（王座）
  if (isThrone(move.to) && piece.type !== 'king') {
    return false;
  }

  // 检查移动规则
  const possibleMoves = getPossibleMovesForPiece(gameState, move.from);
  return possibleMoves.some(m => m.to.row === move.to.row && m.to.col === move.to.col);
};

// 执行移动
export const makeMove = (gameState: TablutGameState, move: TablutMove): TablutGameState => {
  const newBoard = gameState.board.map(row => [...row]);
  const piece = newBoard[move.from.row][move.from.col];

  if (!piece) return gameState;

  // 移动棋子
  newBoard[move.to.row][move.to.col] = piece;
  newBoard[move.from.row][move.from.col] = null;

  // 更新国王位置
  let newKingPosition = gameState.kingPosition;
  if (piece.type === 'king') {
    newKingPosition = move.to;
  }

  // 检查夹吃
  const captures = checkCaptures(newBoard, move.to);

  // 更新被吃棋子
  const newCapturedPieces = { ...gameState.capturedPieces };
  captures.forEach(capturedPiece => {
    newBoard[capturedPiece.position.row][capturedPiece.position.col] = null;
    newCapturedPieces[capturedPiece.piece.player].push(capturedPiece.piece);
  });

  // 创建新的游戏状态
  const newGameState: TablutGameState = {
    ...gameState,
    board: newBoard,
    currentPlayer: gameState.currentPlayer === 'defender' ? 'attacker' : 'defender',
    moves: [...gameState.moves, move],
    gameHistory: [...gameState.gameHistory, gameState],
    kingPosition: newKingPosition,
    capturedPieces: newCapturedPieces,
    totalMoves: gameState.totalMoves + 1,
    captureMoves: gameState.captureMoves + (captures.length > 0 ? 1 : 0),
    selectedPiece: undefined,
    possibleMoves: []
  };

  // 检查游戏结束条件
  const gameEnd = checkGameEnd(newGameState);
  if (gameEnd.isEnd) {
    newGameState.status = 'finished';
    newGameState.winner = gameEnd.winner;
    newGameState.kingEscaped = gameEnd.reason === 'king_escaped';
    newGameState.kingCaptured = gameEnd.reason === 'king_captured';
  }

  return newGameState;
};

// 检查夹吃（简化版 - 只检查直接夹吃）
export const checkCaptures = (board: (TablutPiece | null)[][], movePosition: Position): { piece: TablutPiece; position: Position }[] => {
  const captures: { piece: TablutPiece; position: Position }[] = [];
  const movingPiece = board[movePosition.row][movePosition.col];

  if (!movingPiece) return captures;

  // 只检查四个方向的直接夹吃（相邻格子）
  const directions = [
    { row: -1, col: 0 }, // 上
    { row: 1, col: 0 },  // 下
    { row: 0, col: -1 }, // 左
    { row: 0, col: 1 }   // 右
  ];

  for (const dir of directions) {
    const capturedPiece = checkSimpleCapture(board, movePosition, dir, movingPiece.player);
    if (capturedPiece) {
      captures.push(capturedPiece);
    }
  }

  return captures;
};

// 简化的直接夹吃检查
const checkSimpleCapture = (
  board: (TablutPiece | null)[][],
  movePosition: Position,
  direction: { row: number; col: number },
  movingPlayer: 'defender' | 'attacker'
): { piece: TablutPiece; position: Position } | null => {
  const movingPiece = board[movePosition.row][movePosition.col];
  if (!movingPiece) return null;

  // 检查移动位置相邻的敌方棋子
  const enemyPos = {
    row: movePosition.row + direction.row,
    col: movePosition.col + direction.col
  };

  if (!isValidPosition(enemyPos)) return null;

  const enemyPiece = board[enemyPos.row][enemyPos.col];
  if (!enemyPiece || enemyPiece.player === movingPlayer) return null;

  // 检查敌方棋子另一侧是否有己方棋子或角落
  const capturePos = {
    row: enemyPos.row + direction.row,
    col: enemyPos.col + direction.col
  };

  let hasSupport = false;

  // 萨米板棋规则：角落永远可以作为夹吃的支撑点
  if (isValidPosition(capturePos)) {
    if (isCorner(capturePos)) {
      hasSupport = true;
    } else {
      const capturePiece = board[capturePos.row][capturePos.col];
      if (capturePiece && capturePiece.player === movingPlayer) {
        hasSupport = true;
      }
    }
  }

  if (!hasSupport) return null;

  // 国王特殊规则：需要四面包围才能被捕获
  if (enemyPiece.type === 'king') {
    const surroundingAttackers = countSurroundingAttackers(board, enemyPos);
    if (surroundingAttackers >= 4) {
      return {
        piece: enemyPiece,
        position: enemyPos
      };
    }
  } else {
    // 普通棋子两面夹住即可被捕获
    return {
      piece: enemyPiece,
      position: enemyPos
    };
  }

  return null;
};


// 统计指定位置周围攻击者数量
export const countSurroundingAttackers = (board: (TablutPiece | null)[][], position: Position): number => {
  const surroundingPositions = [
    { row: position.row - 1, col: position.col },     // 上
    { row: position.row + 1, col: position.col },     // 下
    { row: position.row, col: position.col - 1 },     // 左
    { row: position.row, col: position.col + 1 }      // 右
  ];

  let attackerCount = 0;
  for (const pos of surroundingPositions) {
    if (isValidPosition(pos)) {
      const piece = board[pos.row][pos.col];
      if (piece && piece.player === 'attacker') {
        attackerCount++;
      } else if (!isValidPosition(pos)) {
        // 如果是棋盘边界，也算作被围住
        attackerCount++;
      }
    }
  }

  return attackerCount;
};

// 检查游戏是否结束
export const checkGameEnd = (gameState: TablutGameState): { isEnd: boolean; winner: 'defender' | 'attacker' | null; reason: string } => {
  // 检查国王是否到达角落（护王方胜利）
  if (gameState.kingPosition) {
    const isKingInCorner = CORNERS.some(corner =>
      corner.row === gameState.kingPosition.row && corner.col === gameState.kingPosition.col
    );

    if (isKingInCorner) {
      return { isEnd: true, winner: 'defender', reason: 'king_escaped' };
    }
  }

  // 检查国王是否被四面围住（攻击方胜利）
  const kingPos = gameState.kingPosition;
  if (kingPos) {
    const kingPiece = gameState.board[kingPos.row][kingPos.col];

    if (kingPiece && kingPiece.type === 'king') {
      const surroundingAttackers = countSurroundingAttackers(gameState.board, kingPos);

      if (surroundingAttackers === 4) {
        return { isEnd: true, winner: 'attacker', reason: 'king_captured' };
      }
    }
  }

  // 检查是否还有国王（游戏继续的条件）
  const hasKing = gameState.board.some(row =>
    row.some(piece => piece && piece.type === 'king')
  );

  if (!hasKing) {
    return { isEnd: true, winner: 'attacker', reason: 'king_captured' };
  }

  // 检查是否还有可能的移动（避免僵局）
  const currentMoves = getAllPossibleMoves(gameState, gameState.currentPlayer);
  if (currentMoves.length === 0) {
    // 当前玩家无法移动，对方获胜
    const winner = gameState.currentPlayer === 'defender' ? 'attacker' : 'defender';
    return { isEnd: true, winner, reason: 'no_moves' };
  }

  return { isEnd: false, winner: null, reason: '' };
};

// 选择棋子
export const selectPiece = (gameState: TablutGameState, position: Position): TablutGameState => {
  const piece = gameState.board[position.row][position.col];

  if (!piece || piece.player !== gameState.currentPlayer) {
    return deselectPiece(gameState);
  }

  const possibleMoves = getPossibleMovesForPiece(gameState, position);

  return {
    ...gameState,
    selectedPiece: position,
    possibleMoves
  };
};

// 取消选择棋子
export const deselectPiece = (gameState: TablutGameState): TablutGameState => {
  return {
    ...gameState,
    selectedPiece: undefined,
    possibleMoves: []
  };
};

// 悔棋功能
export const undoMove = (gameState: TablutGameState): TablutGameState => {
  if (gameState.gameHistory.length === 0) {
    return gameState;
  }

  const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];
  return {
    ...previousState,
    selectedPiece: undefined,
    possibleMoves: [],
    isAIThinking: false
  };
};

// AI评估函数
export const evaluatePosition = (gameState: TablutGameState, player: 'defender' | 'attacker'): number => {
  let score = 0;

  // 找到国王位置
  let kingPos: Position | null = null;
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      const piece = gameState.board[row][col];
      if (piece && piece.type === 'king') {
        kingPos = { row, col };
        break;
      }
    }
    if (kingPos) break;
  }

  // 国王到角落的距离（对护王方重要）
  if (kingPos) {
    let minCornerDistance = Infinity;
    for (const corner of CORNERS) {
      const distance = Math.abs(kingPos.row - corner.row) + Math.abs(kingPos.col - corner.col);
      minCornerDistance = Math.min(minCornerDistance, distance);
    }

    if (player === 'defender') {
      score -= minCornerDistance * 10; // 距离角落越近越好
    } else {
      score += minCornerDistance * 10;  // 距离角落越远越好
    }
  }

  // 棋子价值评估
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      const piece = gameState.board[row][col];
      if (piece) {
        let pieceValue = 0;

        switch (piece.type) {
          case 'king':
            pieceValue = 1000;
            break;
          case 'guard':
            pieceValue = 50;
            // 守卫离国王越近越有价值
            if (kingPos) {
              const distanceToKing = Math.abs(row - kingPos.row) + Math.abs(col - kingPos.col);
              pieceValue += Math.max(0, 20 - distanceToKing * 2);
            }
            break;
          case 'attacker':
            pieceValue = 30;
            // 攻击者离国王越近越有价值
            if (kingPos) {
              const distanceToKing = Math.abs(row - kingPos.row) + Math.abs(col - kingPos.col);
              pieceValue += Math.max(0, 15 - distanceToKing * 2);
            }
            break;
        }

        if (piece.player === player) {
          score += pieceValue;
        } else {
          score -= pieceValue;
        }
      }
    }
  }

  // 移动性评估
  const playerMoves = getAllPossibleMoves(gameState, player);
  const enemy = player === 'defender' ? 'attacker' : 'defender';
  const enemyMoves = getAllPossibleMoves(gameState, enemy);
  score += (playerMoves.length - enemyMoves.length) * 2;

  return score;
};

export default {
  createInitialGameState,
  getAllPossibleMoves,
  getPossibleMovesForPiece,
  isValidMove,
  makeMove,
  checkCaptures,
  checkGameEnd,
  selectPiece,
  deselectPiece,
  undoMove,
  evaluatePosition
};